import React, { useState, useEffect } from 'react';
import { FiUser, FiPhone, FiMail, FiHome, FiCalendar, FiEdit3, FiSave, FiX, FiCamera, FiMapPin, FiUsers } from 'react-icons/fi';
import { useProfile } from '../../context/ProfileContext';

const OwnerDetails = () => {
  const { ownerDetails, updateOwnerDetails, uploadDocument, saving } = useProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [localOwner, setLocalOwner] = useState(ownerDetails);
  const [localSaving, setLocalSaving] = useState(false);

  // Sync local owner with global owner when it changes
  useEffect(() => {
    setLocalOwner(ownerDetails);
  }, [ownerDetails]);

  const handleInputChange = (field, value, parent = null, subParent = null) => {
    if (subParent && parent) {
      setLocalOwner(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [subParent]: {
            ...prev[parent][subParent],
            [field]: value
          }
        }
      }));
    } else if (parent) {
      setLocalOwner(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [field]: value
        }
      }));
    } else {
      setLocalOwner(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    setLocalSaving(true);
    try {
      await updateOwnerDetails(localOwner);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save owner details:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setLocalSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalOwner(ownerDetails);
    setIsEditing(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const uploadedFile = await uploadDocument('owner', file, 'profilePhoto');
      setLocalOwner(prev => ({
        ...prev,
        profilePhoto: uploadedFile.url
      }));
    }
  };

  const handleSameAsAddress = (checked) => {
    if (checked) {
      setLocalOwner(prev => ({
        ...prev,
        currentAddress: {
          ...prev.permanentAddress,
          sameAsPermanent: true
        }
      }));
    } else {
      setLocalOwner(prev => ({
        ...prev,
        currentAddress: {
          ...prev.currentAddress,
          sameAsPermanent: false
        }
      }));
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Header Section */}
      <div className="glass-card p-4 mb-4">
        <div className="row align-items-center">
          <div className="col-auto">
            <div className="position-relative">
              <div
                className="rounded-circle bg-gradient shadow-lg d-flex align-items-center justify-content-center"
                style={{
                  width: '100px',
                  height: '100px',
                  backgroundImage: localOwner.profilePhoto ? `url(${localOwner.profilePhoto})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  background: !localOwner.profilePhoto ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : undefined
                }}
              >
                {!localOwner.profilePhoto && <FiUser size={40} className="text-white" />}
              </div>
              {isEditing && (
                <label className="position-absolute bottom-0 end-0 btn btn-primary btn-sm rounded-circle p-2">
                  <FiCamera size={16} />
                  <input
                    type="file"
                    className="d-none"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                  />
                </label>
              )}
            </div>
          </div>

          <div className="col">
            <h3 className="fw-bold mb-1">{localOwner.fullName || 'Owner Name'}</h3>
            <p className="text-muted mb-0">
              <FiPhone className="me-2" />
              {localOwner.contactInfo.mobile || 'Add phone number'}
            </p>
            <p className="text-muted mb-0">
              <FiMail className="me-2" />
              {localOwner.contactInfo.email || 'Add email address'}
            </p>
          </div>

          <div className="col-auto">
            {!isEditing ? (
              <button
                className="btn btn-primary rounded-pill px-4"
                onClick={() => setIsEditing(true)}
              >
                <FiEdit3 className="me-2" />
                Edit Details
              </button>
            ) : (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary rounded-pill px-4"
                  onClick={handleCancel}
                >
                  <FiX className="me-2" />
                  Cancel
                </button>
                <button
                  className="btn btn-primary rounded-pill px-4"
                  onClick={handleSave}
                >
                  <FiSave className="me-2" />
                  Save
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Personal Information */}
        <div className="col-lg-6">
          <div className="glass-card p-4 h-100">
            <h5 className="fw-bold mb-4">
              <FiUser className="me-2" />
              Personal Information
            </h5>

            <div className="mb-3">
              <label className="form-label small text-muted">Full Name</label>
              <input
                type="text"
                className="form-control"
                value={localOwner.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                disabled={!isEditing}
                placeholder="Enter full name"
              />
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label small text-muted">Date of Birth</label>
                <div className="input-group">
                  <span className="input-group-text"><FiCalendar /></span>
                  <input
                    type="date"
                    className="form-control"
                    value={localOwner.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    disabled={!isEditing}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted">Gender</label>
                <select
                  className="form-select"
                  value={localOwner.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  disabled={!isEditing}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <label className="form-label small text-muted">Nationality</label>
                <input
                  type="text"
                  className="form-control"
                  value={localOwner.nationality}
                  onChange={(e) => handleInputChange('nationality', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Nationality"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted">Marital Status</label>
                <select
                  className="form-select"
                  value={localOwner.maritalStatus}
                  onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                  disabled={!isEditing}
                >
                  <option value="">Select Status</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </div>
            </div>

            <div className="mt-3">
              <label className="form-label small text-muted">Father's Name</label>
              <input
                type="text"
                className="form-control"
                value={localOwner.fatherName}
                onChange={(e) => handleInputChange('fatherName', e.target.value)}
                disabled={!isEditing}
                placeholder="Enter father's name"
              />
            </div>

            <div className="mt-3">
              <label className="form-label small text-muted">Mother's Name</label>
              <input
                type="text"
                className="form-control"
                value={localOwner.motherName}
                onChange={(e) => handleInputChange('motherName', e.target.value)}
                disabled={!isEditing}
                placeholder="Enter mother's name"
              />
            </div>

            {localOwner.maritalStatus === 'married' && (
              <div className="mt-3">
                <label className="form-label small text-muted">Spouse Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={localOwner.spouseName}
                  onChange={(e) => handleInputChange('spouseName', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Enter spouse name"
                />
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="col-lg-6">
          <div className="glass-card p-4 h-100">
            <h5 className="fw-bold mb-4">
              <FiPhone className="me-2" />
              Contact Information
            </h5>

            <div className="mb-3">
              <label className="form-label small text-muted">Mobile Number</label>
              <div className="input-group">
                <span className="input-group-text"><FiPhone /></span>
                <input
                  type="tel"
                  className="form-control"
                  value={localOwner.contactInfo.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value, 'contactInfo')}
                  disabled={!isEditing}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label small text-muted">Alternate Mobile</label>
              <input
                type="tel"
                className="form-control"
                value={localOwner.contactInfo.alternateMobile}
                onChange={(e) => handleInputChange('alternateMobile', e.target.value, 'contactInfo')}
                disabled={!isEditing}
                placeholder="+91 XXXXX XXXXX"
              />
            </div>

            <div className="mb-3">
              <label className="form-label small text-muted">Email Address</label>
              <div className="input-group">
                <span className="input-group-text"><FiMail /></span>
                <input
                  type="email"
                  className="form-control"
                  value={localOwner.contactInfo.email}
                  onChange={(e) => handleInputChange('email', e.target.value, 'contactInfo')}
                  disabled={!isEditing}
                  placeholder="email@example.com"
                />
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="border-top pt-3 mt-4">
              <h6 className="fw-bold mb-3">
                <FiUsers className="me-2" />
                Emergency Contact
              </h6>

              <div className="mb-3">
                <label className="form-label small text-muted">Contact Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={localOwner.contactInfo.emergencyContact.name}
                  onChange={(e) => handleInputChange('name', e.target.value, 'contactInfo', 'emergencyContact')}
                  disabled={!isEditing}
                  placeholder="Emergency contact name"
                />
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small text-muted">Relation</label>
                  <input
                    type="text"
                    className="form-control"
                    value={localOwner.contactInfo.emergencyContact.relation}
                    onChange={(e) => handleInputChange('relation', e.target.value, 'contactInfo', 'emergencyContact')}
                    disabled={!isEditing}
                    placeholder="Relation"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small text-muted">Phone Number</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={localOwner.contactInfo.emergencyContact.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value, 'contactInfo', 'emergencyContact')}
                    disabled={!isEditing}
                    placeholder="Phone number"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Permanent Address */}
        <div className="col-lg-6">
          <div className="glass-card p-4 h-100">
            <h5 className="fw-bold mb-4">
              <FiHome className="me-2" />
              Permanent Address
            </h5>

            <div className="mb-3">
              <label className="form-label small text-muted">Street Address</label>
              <input
                type="text"
                className="form-control"
                value={localOwner.permanentAddress.street}
                onChange={(e) => handleInputChange('street', e.target.value, 'permanentAddress')}
                disabled={!isEditing}
                placeholder="Enter street address"
              />
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label small text-muted">City</label>
                <input
                  type="text"
                  className="form-control"
                  value={localOwner.permanentAddress.city}
                  onChange={(e) => handleInputChange('city', e.target.value, 'permanentAddress')}
                  disabled={!isEditing}
                  placeholder="City"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted">State</label>
                <input
                  type="text"
                  className="form-control"
                  value={localOwner.permanentAddress.state}
                  onChange={(e) => handleInputChange('state', e.target.value, 'permanentAddress')}
                  disabled={!isEditing}
                  placeholder="State"
                />
              </div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <label className="form-label small text-muted">PIN Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={localOwner.permanentAddress.pincode}
                  onChange={(e) => handleInputChange('pincode', e.target.value, 'permanentAddress')}
                  disabled={!isEditing}
                  placeholder="PIN Code"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted">Country</label>
                <input
                  type="text"
                  className="form-control"
                  value={localOwner.permanentAddress.country}
                  onChange={(e) => handleInputChange('country', e.target.value, 'permanentAddress')}
                  disabled={!isEditing}
                  placeholder="Country"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Current Address */}
        <div className="col-lg-6">
          <div className="glass-card p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold mb-0">
                <FiMapPin className="me-2" />
                Current Address
              </h5>
              {isEditing && (
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="sameAsAddress"
                    checked={localOwner.currentAddress.sameAsPermanent}
                    onChange={(e) => handleSameAsAddress(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="sameAsAddress">
                    Same as permanent
                  </label>
                </div>
              )}
            </div>

            {localOwner.currentAddress.sameAsPermanent ? (
              <div className="text-center py-4 text-muted">
                <FiHome size={40} className="mb-2 opacity-50" />
                <p>Same as permanent address</p>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <label className="form-label small text-muted">Street Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={localOwner.currentAddress.street}
                    onChange={(e) => handleInputChange('street', e.target.value, 'currentAddress')}
                    disabled={!isEditing}
                    placeholder="Enter street address"
                  />
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small text-muted">City</label>
                    <input
                      type="text"
                      className="form-control"
                      value={localOwner.currentAddress.city}
                      onChange={(e) => handleInputChange('city', e.target.value, 'currentAddress')}
                      disabled={!isEditing}
                      placeholder="City"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small text-muted">State</label>
                    <input
                      type="text"
                      className="form-control"
                      value={localOwner.currentAddress.state}
                      onChange={(e) => handleInputChange('state', e.target.value, 'currentAddress')}
                      disabled={!isEditing}
                      placeholder="State"
                    />
                  </div>
                </div>

                <div className="row g-3 mt-1">
                  <div className="col-md-6">
                    <label className="form-label small text-muted">PIN Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={localOwner.currentAddress.pincode}
                      onChange={(e) => handleInputChange('pincode', e.target.value, 'currentAddress')}
                      disabled={!isEditing}
                      placeholder="PIN Code"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small text-muted">Country</label>
                    <input
                      type="text"
                      className="form-control"
                      value={localOwner.currentAddress.country}
                      onChange={(e) => handleInputChange('country', e.target.value, 'currentAddress')}
                      disabled={!isEditing}
                      placeholder="Country"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerDetails;